import serverVars from 'server-vars';
export const chains = new Map([
    [1, 'Mainnet'],
    [3, 'Ropsten'],
    [4, 'Rinkeby'],
    [5, 'Goerli'],
    [42, 'Kovan'],
    [1337, 'localhost'],
]);
export const NODE_ETH_CHAIN_ID = 'NODE_ETH_CHAIN_ID' as const;
export const NODE_ALCHEMY_URL = 'NODE_ALCHEMY_URL' as const;

function chainIdsFromEnv(): string | undefined {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ETH_CHAIN_ID) {
        return process.env.NODE_ETH_CHAIN_ID;
    } else {
        return serverVars.get(NODE_ETH_CHAIN_ID);
    }
}

export function alchemyUrlFromEnv(): string | undefined {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ALCHEMY_URL) {
        return process.env.NODE_ALCHEMY_URL;
    } else {
        return serverVars.get(NODE_ALCHEMY_URL);
    }
}

/**
 * @description Returns an array of chain IDs from the environment
 */
export function supportedChains(): number[] {
    const rawChainId = chainIdsFromEnv();
    if (!rawChainId) {
        throw new Error('NODE_ETH_CHAIN_ID must be defined in the environment.');
    }
    return rawChainId.split(',').map(id => parseInt(id, 10));
}

/**
 * @description If >1 ID found, will disregard 1337 (localhost) and return the first non-localhost ID found
 */
export function getChainIdFromEnv(): number | never {
    const supportedChainIds = supportedChains();
    let chainId = null;
    if (supportedChainIds.length === 1) {
        chainId = supportedChainIds[0];
    } else if (supportedChainIds.length > 1) {
        chainId = supportedChainIds.filter(id => id !== 1337)[0];
    }
    if (!chainId) {
        throw new Error('No supported chains found!');
    }
    return chainId;
}

export function getChainNameFromEnv(): string {
    const chainId = getChainIdFromEnv();
    return chains.get(chainId) || 'Mainnet';
}

/**
 * @description Returns a string of the longer name for the current chain
 */
export function getLongChainName(id: number = getChainIdFromEnv()): string {
    const chainName = chains.get(id) || 'Mainnet';
    if (id === 1) {
        return `Ethereum ${chainName}`;
    } else if (id === 1337) {
        return chainName;
    }
    return `${chainName} Test Network`;
}

/**
 * @description Returns an array of supported chain names from the environment
 */
export function supportedChainNames(): string[] {
    const supportedChainIds = supportedChains();
    return supportedChainIds.reduce((acc: string[], chainId: number) => {
        const chainName = chains.get(chainId)?.toLowerCase();
        if (chainName) {
            return [...acc, chainName];
        }
        return acc;
    }, []);
}

/**
 * @description Returns an object of the supported chain from the environment in the appropriate style for WalletConnect
 */
export function supportedChainRPC(): Record<number, string> | undefined {
    const supportedChainId = getChainIdFromEnv();
    const supportedChainName = getChainNameFromEnv();
    const alchemyUrl = alchemyUrlFromEnv();
    if (alchemyUrl && alchemyUrl.includes(supportedChainName.toLowerCase())) {
        return { [supportedChainId]: alchemyUrl };
    } else {
        return undefined;
    }
}
